import {
  createRootRoute, createRoute, createRouter, redirect, Outlet, Link,
} from '@tanstack/react-router';
import { getAccessToken } from './lib/api';
import { refresh } from './lib/auth';
import { AppShell } from './shell/AppShell';
import { LoginPage } from './routes/Login';
import { DashboardPage } from './routes/Dashboard';
import { ItemsPage } from './routes/Items';
import { NotFoundPage } from './routes/NotFound';
import { SettingsLayout } from './routes/settings/SettingsLayout';
import { ListView } from './routes/_ListView';
import { ModuleLanding } from './routes/ModuleLanding';
import { ModuleStub } from './routes/ModuleStub';
import { SalesInvoiceForm } from './routes/SalesInvoiceForm';
import { PurchaseInvoiceForm } from './routes/PurchaseInvoiceForm';
import { PurchaseOrderForm } from './routes/PurchaseOrderForm';
import { MaterialRequestForm } from './routes/MaterialRequestForm';
import { OpportunityBoard } from './routes/OpportunityBoard';
import { Companies } from './routes/Companies';
import { PurchaseOrderBoard, MaterialRequestBoard } from './routes/PurchaseOrderBoard';
import { Button } from './components/Button';
import { Columns3 } from 'lucide-react';
import { PurchaseReceiptForm } from './routes/PurchaseReceiptForm';
import { StockEntryForm } from './routes/StockEntryForm';
import { JournalEntryForm } from './routes/JournalEntryForm';
import { PaymentEntryForm } from './routes/PaymentEntryForm';
import { UOMQuarantine } from './routes/UOMQuarantine';
import { Help } from './routes/Help';
import { UserGuide } from './routes/UserGuide';
import { AssetValueAdjustmentForm } from './routes/AssetValueAdjustmentForm';
import { DepreciationRun } from './routes/DepreciationRun';
import { TaskForm } from './routes/TaskForm';
import { TaskTemplateForm } from './routes/TaskTemplateForm';
import { TimesheetForm } from './routes/TimesheetForm';
import { SalesOrderForm } from './routes/SalesOrderForm';
import { POSRegister } from './routes/POSRegister';
import { ReportsPage } from './routes/Reports';
import { CreateFormPage } from './routes/CreateFormPage';
import { DetailView } from './routes/_DetailView';
import { MigrationWizard, MigrationWizardEntry } from './routes/MigrationWizard';
import { doctypes, modules } from './lib/doctypes';
import { getCreateSchema } from './lib/createSchema';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async () => {
    if (!getAccessToken()) {
      const r = await refresh();
      if (!r) throw redirect({ to: '/login' });
    }
  },
  component: AppShell,
});

// Setup wizard runs full-screen, no AppShell — auth-gated but no sidebar.
const setupParent = createRoute({
  getParentRoute: () => rootRoute,
  id: 'setup-parent',
  beforeLoad: async () => {
    if (!getAccessToken()) {
      const r = await refresh();
      if (!r) throw redirect({ to: '/login' });
    }
  },
  component: Outlet,
});
const setupEntryRoute  = createRoute({ getParentRoute: () => setupParent, path: '/setup',              component: MigrationWizardEntry });
const setupSessionRoute = createRoute({ getParentRoute: () => setupParent, path: '/setup/$sessionId',  component: MigrationWizard });

// Static routes
const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: '/',                          component: DashboardPage });
const itemsRoute     = createRoute({ getParentRoute: () => appRoute, path: '/accounting/items',          component: ItemsPage });

// Full forms
const siNewRoute  = createRoute({ getParentRoute: () => appRoute, path: '/accounting/sales-invoices/new',   component: SalesInvoiceForm });
const siEditRoute = createRoute({ getParentRoute: () => appRoute, path: '/accounting/sales-invoices/$id',   component: SalesInvoiceForm });
const piNewRoute  = createRoute({ getParentRoute: () => appRoute, path: '/accounting/purchase-invoices/new', component: PurchaseInvoiceForm });
const piEditRoute = createRoute({ getParentRoute: () => appRoute, path: '/accounting/purchase-invoices/$id', component: PurchaseInvoiceForm });
const poNewRoute  = createRoute({ getParentRoute: () => appRoute, path: '/accounting/purchase-orders/new',   component: PurchaseOrderForm });
const poEditRoute = createRoute({ getParentRoute: () => appRoute, path: '/accounting/purchase-orders/$id',   component: PurchaseOrderForm });
const oppBoardRoute = createRoute({ getParentRoute: () => appRoute, path: '/crm/opportunities/board', component: OpportunityBoard });
const companiesRoute = createRoute({ getParentRoute: () => appRoute, path: '/crm/companies',           component: Companies });
const poBoardRoute = createRoute({ getParentRoute: () => appRoute, path: '/accounting/purchase-orders/board',   component: PurchaseOrderBoard });
const mrBoardRoute = createRoute({ getParentRoute: () => appRoute, path: '/accounting/material-requests/board', component: MaterialRequestBoard });
const mrNewRoute  = createRoute({ getParentRoute: () => appRoute, path: '/accounting/material-requests/new', component: MaterialRequestForm });
const mrEditRoute = createRoute({ getParentRoute: () => appRoute, path: '/accounting/material-requests/$id', component: MaterialRequestForm });
const grnNewRoute  = createRoute({ getParentRoute: () => appRoute, path: '/stock/purchase-receipts/new', component: PurchaseReceiptForm });
const grnEditRoute = createRoute({ getParentRoute: () => appRoute, path: '/stock/purchase-receipts/$id', component: PurchaseReceiptForm });
const seNewRoute   = createRoute({ getParentRoute: () => appRoute, path: '/stock/stock-entries/new', component: StockEntryForm });
const seEditRoute  = createRoute({ getParentRoute: () => appRoute, path: '/stock/stock-entries/$id', component: StockEntryForm });
const jeNewRoute  = createRoute({ getParentRoute: () => appRoute, path: '/accounting/journal-entries/new',  component: JournalEntryForm });
const jeEditRoute = createRoute({ getParentRoute: () => appRoute, path: '/accounting/journal-entries/$id',  component: JournalEntryForm });
const peNewRoute  = createRoute({ getParentRoute: () => appRoute, path: '/accounting/payment-entries/new',   component: PaymentEntryForm });
const peEditRoute = createRoute({ getParentRoute: () => appRoute, path: '/accounting/payment-entries/$id',   component: PaymentEntryForm });
const avaNewRoute  = createRoute({ getParentRoute: () => appRoute, path: '/assets/asset-value-adjustments/new', component: AssetValueAdjustmentForm });
const avaEditRoute = createRoute({ getParentRoute: () => appRoute, path: '/assets/asset-value-adjustments/$id', component: AssetValueAdjustmentForm });
const depRunRoute  = createRoute({ getParentRoute: () => appRoute, path: '/assets/depreciation-run', component: DepreciationRun });
const taskNewRoute  = createRoute({ getParentRoute: () => appRoute, path: '/projects/tasks/new', component: TaskForm });
const taskEditRoute = createRoute({ getParentRoute: () => appRoute, path: '/projects/tasks/$id', component: TaskForm });
const ttNewRoute    = createRoute({ getParentRoute: () => appRoute, path: '/projects/task-templates/new', component: TaskTemplateForm });
const ttEditRoute   = createRoute({ getParentRoute: () => appRoute, path: '/projects/task-templates/$id', component: TaskTemplateForm });
const tsNewRoute    = createRoute({ getParentRoute: () => appRoute, path: '/projects/timesheets/new', component: TimesheetForm });
const tsEditRoute   = createRoute({ getParentRoute: () => appRoute, path: '/projects/timesheets/$id', component: TimesheetForm });
const soNewRoute    = createRoute({ getParentRoute: () => appRoute, path: '/accounting/sales-orders/new', component: SalesOrderForm });
const soEditRoute   = createRoute({ getParentRoute: () => appRoute, path: '/accounting/sales-orders/$id', component: SalesOrderForm });
const posRegisterRoute = createRoute({ getParentRoute: () => appRoute, path: '/pos/invoices', component: POSRegister });

// Reports
const reportsIndex = createRoute({ getParentRoute: () => appRoute, path: '/accounting/reports',       component: ReportsPage });
const reportsKind  = createRoute({ getParentRoute: () => appRoute, path: '/accounting/reports/$slug', component: ReportsPage });

// Auto-built doctype list routes (skip the ones with custom pages/forms).
const SKIP_LIST = new Set([
  '/accounting/items',              // ItemsPage
  '/accounting/sales-invoices',     // (list registered separately so SI's `new` route doesn't collide)
  '/accounting/purchase-invoices',  // PI bespoke form needs its own list registration too
  '/accounting/purchase-orders',    // PO same pattern
  '/accounting/material-requests',  // MR same pattern
  '/stock/purchase-receipts',       // GRN same pattern
  '/accounting/journal-entries',    // same as SI
  '/crm/opportunities',             // bespoke list — adds Board view toggle
  '/crm/companies',                 // synthetic — unified customer + supplier view
  '/stock/uoms',                    // bespoke list — adds Quarantine inbox button
  '/accounting/tax-categories',     // list-only (no GET /{id})
  '/assets/finance-books',          // list-only (no GET /{id}); inline delete
  '/assets/assets',                 // bespoke list — adds "Run depreciation" action
  '/pos/invoices',                  // bespoke register page (single-step create+submit)
]);

const doctypeListRoutes = Object.values(doctypes)
  .filter((dt) => !SKIP_LIST.has(`${dt.modulePath}/${dt.slug}`))
  .map((dt) =>
    createRoute({
      getParentRoute: () => appRoute,
      path: `${dt.modulePath}/${dt.slug}`,
      component: () => <ListView config={dt} />,
    }),
  );

// SI + JE need their own list routes since we skipped them above.
const siListRoute = createRoute({ getParentRoute: () => appRoute, path: '/accounting/sales-invoices',    component: () => <ListView config={doctypes.salesInvoices!} /> });
const piListRoute = createRoute({ getParentRoute: () => appRoute, path: '/accounting/purchase-invoices', component: () => <ListView config={doctypes.purchaseInvoices!} /> });
const poListRoute = createRoute({
  getParentRoute: () => appRoute, path: '/accounting/purchase-orders',
  component: () => (
    <ListView config={doctypes.purchaseOrders!}
      extraActions={
        <Button variant="secondary" asChild>
          <Link to={'/accounting/purchase-orders/board' as never}><Columns3 className="size-4" /> Board</Link>
        </Button>
      } />
  ),
});
const mrListRoute = createRoute({
  getParentRoute: () => appRoute, path: '/accounting/material-requests',
  component: () => (
    <ListView config={doctypes.materialRequests!}
      extraActions={
        <Button variant="secondary" asChild>
          <Link to={'/accounting/material-requests/board' as never}><Columns3 className="size-4" /> Board</Link>
        </Button>
      } />
  ),
});
const grnListRoute = createRoute({ getParentRoute: () => appRoute, path: '/stock/purchase-receipts',     component: () => <ListView config={doctypes.purchaseReceipts!} /> });
const jeListRoute = createRoute({ getParentRoute: () => appRoute, path: '/accounting/journal-entries',   component: () => <ListView config={doctypes.journalEntries!} /> });
const oppListRoute = createRoute({
  getParentRoute: () => appRoute, path: '/crm/opportunities',
  component: () => (
    <ListView config={doctypes.opportunities!}
      extraActions={
        <Button variant="secondary" asChild>
          <Link to={'/crm/opportunities/board' as never}><Columns3 className="size-4" /> Board</Link>
        </Button>
      } />
  ),
});
const uomListRoute = createRoute({
  getParentRoute: () => appRoute, path: '/stock/uoms',
  component: () => (
    <ListView config={doctypes.uoms!}
      onRowClick={() => {}}
      extraActions={
        <Button variant="secondary" asChild>
          <Link to={'/stock/uoms/quarantine' as never}>Quarantine inbox</Link>
        </Button>
      } />
  ),
});
const uomQuarantineRoute = createRoute({
  getParentRoute: () => appRoute, path: '/stock/uoms/quarantine', component: UOMQuarantine,
});
const taxCatListRoute = createRoute({
  getParentRoute: () => appRoute, path: '/accounting/tax-categories',
  component: () => <ListView config={doctypes.taxCategories!} onRowClick={() => {}} />,
});
const financeBooksListRoute = createRoute({
  getParentRoute: () => appRoute, path: '/assets/finance-books',
  component: () => <ListView config={doctypes.financeBooks!} onRowClick={() => {}} />,
});
const assetsListRoute = createRoute({
  getParentRoute: () => appRoute, path: '/assets/assets',
  component: () => (
    <ListView config={doctypes.assets!}
      extraActions={
        <Button variant="secondary" asChild>
          <Link to={'/assets/depreciation-run' as never}>Run depreciation</Link>
        </Button>
      } />
  ),
});

// Auto-built /new routes for every doctype that has a create schema and no bespoke form.
const BESPOKE_NEW = new Set(['/accounting/sales-invoices', '/accounting/purchase-invoices', '/accounting/purchase-orders', '/accounting/material-requests', '/stock/purchase-receipts', '/accounting/journal-entries', '/accounting/payment-entries', '/assets/asset-value-adjustments', '/projects/tasks', '/projects/task-templates', '/projects/timesheets', '/accounting/sales-orders']);

const doctypeNewRoutes = Object.values(doctypes)
  .filter((dt) => !BESPOKE_NEW.has(`${dt.modulePath}/${dt.slug}`) && !!getCreateSchema(dt.modulePath, dt.slug))
  .map((dt) => {
    const schema = getCreateSchema(dt.modulePath, dt.slug)!;
    return createRoute({
      getParentRoute: () => appRoute,
      path: `${dt.modulePath}/${dt.slug}/new`,
      component: () => <CreateFormPage config={dt} schema={schema} />,
    });
  });

// Doctypes whose backend doesn't expose a `GET /{id}` — we skip the detail
// + edit auto-routes so we don't strand users on a 404 page. They keep the
// list and /new routes; deletion / edits happen via the list page.
const NO_DETAIL = new Set([
  '/accounting/tax-categories',
  '/stock/uoms',
  '/assets/finance-books',
]);

// Auto-built /{id} detail routes for every non-bespoke doctype. SI/PI/JE have
// their own bespoke forms at /$id that double as detail pages, so they're
// skipped here.
const doctypeDetailRoutes = Object.values(doctypes)
  .filter((dt) => !BESPOKE_NEW.has(`${dt.modulePath}/${dt.slug}`)
               && !NO_DETAIL.has(`${dt.modulePath}/${dt.slug}`)
               && !!getCreateSchema(dt.modulePath, dt.slug))
  .map((dt) => {
    const schema = getCreateSchema(dt.modulePath, dt.slug)!;
    return createRoute({
      getParentRoute: () => appRoute,
      path: `${dt.modulePath}/${dt.slug}/$id`,
      component: () => <DetailView config={dt} schema={schema} />,
    });
  });

// Auto-built /{id}/edit routes — reuses CreateFormPage in edit mode.
const doctypeEditRoutes = Object.values(doctypes)
  .filter((dt) => !BESPOKE_NEW.has(`${dt.modulePath}/${dt.slug}`)
               && !NO_DETAIL.has(`${dt.modulePath}/${dt.slug}`)
               && !!getCreateSchema(dt.modulePath, dt.slug))
  .map((dt) => {
    const schema = getCreateSchema(dt.modulePath, dt.slug)!;
    return createRoute({
      getParentRoute: () => appRoute,
      path: `${dt.modulePath}/${dt.slug}/$id/edit`,
      component: () => <CreateFormPage config={dt} schema={schema} editMode />,
    });
  });

const moduleLandingRoutes = modules.map((m) =>
  createRoute({
    getParentRoute: () => appRoute,
    path: m.path,
    component: () => <ModuleLanding modulePath={m.path} />,
  }),
);

const settingsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  beforeLoad: () => { throw redirect({ to: '/settings/$section' as never, params: { section: 'appearance' } as never }); },
});
const settingsSectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/$section',
  component: SettingsLayout,
});
const helpRoute = createRoute({ getParentRoute: () => appRoute, path: '/help', component: Help });
const userGuideRoute = createRoute({ getParentRoute: () => appRoute, path: '/user-guide', component: UserGuide });

const routeTree = rootRoute.addChildren([
  loginRoute,
  setupParent.addChildren([setupEntryRoute, setupSessionRoute]),
  appRoute.addChildren([
    dashboardRoute,
    itemsRoute,
    siNewRoute, siEditRoute, siListRoute,
    piNewRoute, piEditRoute, piListRoute,
    poNewRoute, poEditRoute, poListRoute,
    mrNewRoute, mrEditRoute, mrListRoute,
    oppBoardRoute, oppListRoute, companiesRoute,
    poBoardRoute, mrBoardRoute,
    grnNewRoute, grnEditRoute, grnListRoute,
    seNewRoute, seEditRoute,
    jeNewRoute, jeEditRoute, jeListRoute,
    peNewRoute, peEditRoute,
    avaNewRoute, avaEditRoute, depRunRoute,
    taskNewRoute, taskEditRoute, ttNewRoute, ttEditRoute, tsNewRoute, tsEditRoute,
    soNewRoute, soEditRoute,
    posRegisterRoute,
    uomListRoute, uomQuarantineRoute, taxCatListRoute, financeBooksListRoute, assetsListRoute,
    reportsIndex, reportsKind,
    ...doctypeListRoutes,
    ...doctypeNewRoutes,
    ...doctypeDetailRoutes,
    ...doctypeEditRoutes,
    ...moduleLandingRoutes,
    settingsIndexRoute, settingsSectionRoute, helpRoute, userGuideRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: NotFoundPage,
});

declare module '@tanstack/react-router' {
  interface Register { router: typeof router; }
}
